import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  Avatar,
  Alert,
  LinearProgress,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAppContext } from './GrowthSessionManagement';
import { updateGrowthSession as updateGrowthSessionAPI } from '../Queries';

const ViewGrowthSessionDialog = ({ open, onClose, viewingGrowthSession }) => {
  const { updateGrowthSession, users, getLeadById, getManagerById } = useAppContext();
  const theme = useTheme();
  const [managerComments, setManagerComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentUser = localStorage.getItem("user");
  const userRole = localStorage.getItem("userRole");

  useEffect(() => {
    if (viewingGrowthSession) {
      setManagerComments(viewingGrowthSession.manager_comments || '');
    }
    setError('');
  }, [viewingGrowthSession, open]);

  const handleSaveComments = async () => {
    try {
      setLoading(true);
      setError('');

      const updatedData = {
        ...viewingGrowthSession,
        manager_comments: managerComments,
        updated_at: new Date().toISOString()
      };

      await updateGrowthSessionAPI({
        id: viewingGrowthSession.id,
        manager_comments: managerComments,
        updated_at: updatedData.updated_at
      });
      
      updateGrowthSession(viewingGrowthSession.id, updatedData);
      onClose();
    } catch (error) {
      console.error('Error saving manager comments:', error);
      setError('An error occurred while saving comments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!viewingGrowthSession) return null;

  const sessionData = typeof viewingGrowthSession.session_data === 'string' ? 
    JSON.parse(viewingGrowthSession.session_data) : viewingGrowthSession.session_data || {};
  
  const user = users.find(u => u.id === viewingGrowthSession.user_id || u.email === viewingGrowthSession.user_id);
  const lead = getLeadById && getLeadById(viewingGrowthSession.lead_id);
  const manager = getManagerById && getManagerById(viewingGrowthSession.manager_id);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
        color: 'white',
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
          <VisibilityIcon sx={{ fontSize: 22, color: 'white' }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.95rem', mb: 0.5 }}>
            Growth Conversation
          </Typography>
          {user && (
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
              {user.name} ({user.email})
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1, fontSize: '0.875rem' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {error}
            </Typography>
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <Grid container spacing={2}>
          {/* Enhanced Session Overview */}
          <Grid item xs={12}>
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 1, 
                background: 'linear-gradient(135deg, rgba(10, 0, 104, 0.02) 0%, rgba(204, 64, 73, 0.02) 100%)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box sx={{
                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                color: 'white',
                p: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 22 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                      Session Overview
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                      Growth conversation details and timeline
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(10, 0, 104, 0.04)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#0a0068', width: 26, height: 26 }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0a0068' }}>
                          Participant
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        {user?.name || 'Unknown User'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {user?.email || 'No email available'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(204, 64, 73, 0.04)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#cc4049', width: 26, height: 26 }}>
                          <TrendingUpIcon sx={{ fontSize: 14, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#cc4049' }}>
                          Lead Mentor
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        {lead?.name || 'Not assigned'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {lead?.email || 'No lead assigned'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.04)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#ff9800', width: 26, height: 26 }}>
                          <AssignmentIcon sx={{ fontSize: 14, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#ff9800' }}>
                          Assigned Manager
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        {manager?.name || 'Not assigned'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {manager?.email || 'No manager assigned'}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.04)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#4caf50', width: 26, height: 26 }}>
                          <ScheduleIcon sx={{ fontSize: 14, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#4caf50' }}>
                          Session Date
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        {formatDate(viewingGrowthSession.date)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.04)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#9c27b0', width: 26, height: 26 }}>
                          <CalendarTodayIcon sx={{ fontSize: 14, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#9c27b0' }}>
                          Review Period
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        {formatDate(viewingGrowthSession.review_period_from)} - {formatDate(viewingGrowthSession.review_period_to)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Enhanced Feedback Details */}
          <Grid item xs={12}>
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 1,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Box sx={{
                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                color: 'white',
                p: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CommentIcon sx={{ fontSize: 22 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                      Growth Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                      Detailed feedback and development insights
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#4caf50', width: 24, height: 24 }}>
                          <StarIcon sx={{ fontSize: 12, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#4caf50' }}>
                          Key Strengths
                        </Typography>
                      </Box>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(76, 175, 80, 0.08)', 
                          borderRadius: 2,
                          border: '1px solid rgba(76, 175, 80, 0.2)',
                          minHeight: 80
                        }}
                      >
                        <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 }}>
                          {sessionData.key_strengths || 'Not provided yet'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#ff9800', width: 24, height: 24 }}>
                          <TrendingUpIcon sx={{ fontSize: 12, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#ff9800' }}>
                          Areas for Improvement
                        </Typography>
                      </Box>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(255, 152, 0, 0.08)', 
                          borderRadius: 2,
                          border: '1px solid rgba(255, 152, 0, 0.2)',
                          minHeight: 80
                        }}
                      >
                        <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 }}>
                          {sessionData.areas_for_improvement || 'Not provided yet'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#2196f3', width: 24, height: 24 }}>
                          <AssignmentIcon sx={{ fontSize: 12, color: 'white' }} />
                        </Avatar>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2196f3' }}>
                          Recommended Action Plan
                        </Typography>
                      </Box>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(33, 150, 243, 0.08)', 
                          borderRadius: 2,
                          border: '1px solid rgba(33, 150, 243, 0.2)',
                          minHeight: 60
                        }}
                      >
                        <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 }}>
                          {sessionData.recommended_action_plan || 'Not provided yet'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                  {sessionData.support_required && (
                    <Grid item xs={12}>
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar sx={{ bgcolor: '#9c27b0', width: 24, height: 24 }}>
                            <InfoIcon sx={{ fontSize: 12, color: 'white' }} />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#9c27b0' }}>
                            Support Required
                          </Typography>
                        </Box>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(156, 39, 176, 0.08)', 
                            borderRadius: 2,
                            border: '1px solid rgba(156, 39, 176, 0.2)'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 }}>
                            {sessionData.support_required}
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                  )}
                  {sessionData.suggested_plan_of_action && (
                    <Grid item xs={12}>
                      <Box sx={{ mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Avatar sx={{ bgcolor: '#607d8b', width: 24, height: 24 }}>
                            <TrendingUpIcon sx={{ fontSize: 12, color: 'white' }} />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#607d8b' }}>
                            Suggested Plan of Action
                          </Typography>
                        </Box>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(96, 125, 139, 0.08)', 
                            borderRadius: 2,
                            border: '1px solid rgba(96, 125, 139, 0.2)'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 }}>
                            {sessionData.suggested_plan_of_action}
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Enhanced Dynamic Fields */}
          {sessionData.dynamic_fields && sessionData.dynamic_fields.length > 0 && (
            <Grid item xs={12}>
              <Card 
                variant="outlined" 
                sx={{ 
                  mb: 1,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #607d8b 0%, #455a64 100%)',
                  color: 'white',
                  p: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <InfoIcon sx={{ fontSize: 22 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        Additional Information
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                        Supplementary details and context
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={2}>
                    {sessionData.dynamic_fields.map((field, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(96, 125, 139, 0.05)', 
                            borderRadius: 2,
                            border: '1px solid rgba(96, 125, 139, 0.1)'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            {field.label}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '0.75rem', fontWeight: 500, mt: 0.1 }}>
                            {field.value}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Enhanced Manager Comments Section - Only show for Manager/Mentor */}
          {(userRole === "Manager" || userRole === "Mentor") && (
            <Grid item xs={12}>
              <Card 
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden'
                }}
              >
                <Box sx={{
                  background: 'linear-gradient(135deg, #e91e63 0%, #ad1457 100%)',
                  color: 'white',
                  p: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CommentIcon sx={{ fontSize: 22 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                        Manager Comments
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                        Add your observations and recommendations
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Manager Comments"
                      value={managerComments}
                      onChange={(e) => setManagerComments(e.target.value)}
                      placeholder="Share your insights, observations, and recommendations for this Growth Conversation..."
                      variant="outlined"
                      InputLabelProps={{ 
                        sx: { 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          color: '#e91e63',
                          backgroundColor: 'white',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#e91e63'
                          }
                        } 
                      }}
                      InputProps={{
                        sx: { 
                          fontSize: '0.75rem',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e91e63',
                            borderWidth: 1
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e91e63',
                            borderWidth: 2
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e91e63',
                            borderWidth: 2
                          }
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-input::placeholder': {
                          fontSize: '0.7rem',
                          opacity: 0.7,
                          fontStyle: 'italic'
                        }
                      }}
                    />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                      Your comments will be visible to all stakeholders
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleSaveComments}
                      disabled={loading}
                      startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 2.5,
                        py: 1,
                        background: 'linear-gradient(135deg, #e91e63 0%, #ad1457 100%)',
                        boxShadow: 1,
                        '&:hover': {
                          boxShadow: 2,
                          background: 'linear-gradient(135deg, #ad1457 0%, #880e4f 100%)'
                        },
                        '&:disabled': {
                          background: 'rgba(0,0,0,0.12)',
                          color: 'rgba(0,0,0,0.26)'
                        }
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Comments'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* PIP Details Section - Show when status is pip_created */}
          {viewingGrowthSession.status === 'pip_created' && (() => {
            const pipData = (() => {
              if (!viewingGrowthSession.pip_data) return null;
              try {
                return typeof viewingGrowthSession.pip_data === 'string' 
                  ? JSON.parse(viewingGrowthSession.pip_data) 
                  : viewingGrowthSession.pip_data;
              } catch (error) {
                console.error('Error parsing PIP data:', error);
                return null;
              }
            })();
            
            return (
              <Grid item xs={12}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    mb: 1,
                    borderRadius: 2,
                    border: `2px solid ${theme.palette.warning.main}`,
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    color: 'white',
                    p: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <WarningIcon sx={{ fontSize: 22 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.3 }}>
                          Performance Improvement Plan (PIP)
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                          Detailed improvement plan and requirements
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 2.5 }}>
                    <Grid container spacing={2}>
                      {!pipData || (!pipData.final_decision && !pipData.location && !pipData.pip_start_date && 
                        !pipData.pip_end_date && (!pipData.attached_files || pipData.attached_files.length === 0)) ? (
                        <Grid item xs={12}>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(255, 152, 0, 0.08)', 
                              borderRadius: 2,
                              border: '1px solid rgba(255, 152, 0, 0.2)',
                              textAlign: 'center'
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 20, color: theme.palette.warning.main, mb: 1 }} />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>
                              PIP has been initiated but details are not available yet.
                            </Typography>
                          </Paper>
                        </Grid>
                      ) : (
                        <>
                          {pipData?.final_decision && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Avatar sx={{ bgcolor: '#f44336', width: 24, height: 24 }}>
                                    <AssignmentIcon sx={{ fontSize: 12, color: 'white' }} />
                                  </Avatar>
                                  <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#f44336' }}>
                                    Final Decision
                                  </Typography>
                                </Box>
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: 'rgba(244, 67, 54, 0.08)', 
                                    borderRadius: 2,
                                    border: '1px solid rgba(244, 67, 54, 0.2)',
                                    minHeight: 60
                                  }}
                                >
                                  <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 500 }}>
                                    {pipData.final_decision}
                                  </Typography>
                                </Paper>
                              </Box>
                            </Grid>
                          )}
                          {pipData?.location && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Avatar sx={{ bgcolor: '#9c27b0', width: 24, height: 24 }}>
                                    <InfoIcon sx={{ fontSize: 12, color: 'white' }} />
                                  </Avatar>
                                  <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#9c27b0' }}>
                                    Location/Department
                                  </Typography>
                                </Box>
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: 'rgba(156, 39, 176, 0.08)', 
                                    borderRadius: 2,
                                    border: '1px solid rgba(156, 39, 176, 0.2)',
                                    minHeight: 60
                                  }}
                                >
                                  <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 500 }}>
                                    {pipData.location}
                                  </Typography>
                                </Paper>
                              </Box>
                            </Grid>
                          )}
                          {(pipData?.pip_start_date || pipData?.pip_end_date) && (
                            <Grid item xs={12}>
                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Avatar sx={{ bgcolor: '#3f51b5', width: 24, height: 24 }}>
                                    <ScheduleIcon sx={{ fontSize: 12, color: 'white' }} />
                                  </Avatar>
                                  <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#3f51b5' }}>
                                    PIP Duration
                                  </Typography>
                                </Box>
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: 'rgba(63, 81, 181, 0.08)', 
                                    borderRadius: 2,
                                    border: '1px solid rgba(63, 81, 181, 0.2)'
                                  }}
                                >
                                  <Typography variant="body1" sx={{ fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 500 }}>
                                    {formatDate(pipData?.pip_start_date)} - {formatDate(pipData?.pip_end_date)}
                                  </Typography>
                                </Paper>
                              </Box>
                            </Grid>
                          )}
                          {pipData?.attached_files && pipData.attached_files.length > 0 && (
                            <Grid item xs={12}>
                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Avatar sx={{ bgcolor: '#607d8b', width: 24, height: 24 }}>
                                    <AttachFileIcon sx={{ fontSize: 12, color: 'white' }} />
                                  </Avatar>
                                  <Typography variant="subtitle1" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#607d8b' }}>
                                    Attached Files
                                  </Typography>
                                </Box>
                                <Paper 
                                  elevation={0} 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: 'rgba(96, 125, 139, 0.08)', 
                                    borderRadius: 2,
                                    border: '1px solid rgba(96, 125, 139, 0.2)'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {pipData.attached_files.map((file, index) => {
                                      const fileName = typeof file === 'object' ? file.name : file;
                                      const fileUrl = typeof file === 'object' ? file.url : file;
                                      
                                      return (
                                        <Button
                                          key={index}
                                          variant="outlined"
                                          size="small"
                                          onClick={() => {
                                            if (fileUrl) {
                                              window.open(fileUrl, '_blank');
                                            } else {
                                              console.warn('No URL available for file:', file);
                                            }
                                          }}
                                          startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                          sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            bgcolor: 'rgba(96, 125, 139, 0.05)',
                                            borderColor: 'rgba(96, 125, 139, 0.3)',
                                            color: '#607d8b',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1,
                                            '&:hover': {
                                              bgcolor: 'rgba(96, 125, 139, 0.1)',
                                              borderColor: '#607d8b',
                                              transform: 'scale(1.02)'
                                            },
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          {fileName || 'Download'}
                                        </Button>
                                      );
                                    })}
                                  </Box>
                                </Paper>
                              </Box>
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })()}


        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
        <Button
          onClick={onClose}
          color="secondary"
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            minWidth: 100
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewGrowthSessionDialog;